import Grid from "@material-ui/core/Grid";
import React from "react";
import StringFormat from "../../../util/string-format";
import CardButton from "./CardButton";
import BaseKeyListener from "./listeners/BaseKeyListener";
import dynamicActionsStyles from "../styles/DynamicActions.styles";

// Row widths the grid is allowed to use, widest first.
const ROW_WIDTHS = [4, 3];

const DynamicActions = ({
	columns = 4,
	buttonSize,
	textAlign,
	transitions = [],
	onSelect,
	currentTransition,
	hideIcons,
	disableKeyListener = false,
	fitHeight = false
}) => {
	const classes = dynamicActionsStyles();

	const visible = transitions.filter((t) => !t.showInToolbar);

	// Core flags one action with showAtBottom as the one to pull out of the
	// grid and show as a short full-width bar underneath it. That is only worth
	// doing when it makes the rows come out even: seven actions become two rows
	// of three plus the bar, five become one row of four plus the bar, while
	// eight already fill two rows of four and keep the flagged action inline.
	// When neither count divides evenly the caller's column count stands and
	// the short row is padded with spacers.
	const fittedColumns = (count) =>
		ROW_WIDTHS.find((width) => count > 0 && count % width === 0);

	const bottomTransition = visible.find((t) => t.showAtBottom);
	const inlineColumns = bottomTransition && fittedColumns(visible.length);
	const barColumns =
		bottomTransition && !inlineColumns && fittedColumns(visible.length - 1);

	const barTransition = barColumns ? bottomTransition : null;
	const gridColumns = inlineColumns || barColumns || columns;
	const sm = 12 / gridColumns;
	const gridTransitions = barTransition
		? visible.filter((t) => t.name !== barTransition.name)
		: visible;

	const getButtonText = (transition) => {
		return StringFormat.formatTextWithKeystroke(
			transition.displayName,
			transition.keyStroke
		);
	};

	const renderCard = (transition, extraProps = {}) => (
		<CardButton
			id={transition.name}
			size={buttonSize}
			textAlign={textAlign}
			text={getButtonText(transition)}
			icon={!hideIcons && transition.icon}
			onClick={() => onSelect(transition.name)}
			active={currentTransition === transition.name}
			team={transition.team}
			{...extraProps}
		/>
	);

	// BaseKeyListener also renders the hotkey hint, so it gets a place in the
	// layout rather than being an afterthought: it has to stay visible instead
	// of scrolling out of view.
	const keyListener = !disableKeyListener && (
		<BaseKeyListener items={transitions} onSelected={(t) => onSelect(t.name)} />
	);

	// Rows of `gridColumns` cards each, with the short last row padded out by
	// spacers so its cards keep the same width as a full row's rather than
	// stretching across the gap.
	const rows = [];
	for (let i = 0; i < gridTransitions.length; i += gridColumns) {
		const cards = gridTransitions.slice(i, i + gridColumns);
		const key = cards.map((t) => t.name).join("-");
		const spacerKeys = Array.from(
			{ length: gridColumns - cards.length },
			(unused, offset) => `${key}-spacer-${offset}`
		);
		rows.push({ key, cards, spacerKeys });
	}

	const fitLayout = (
		<div className={classes.fitRoot}>
			{rows.map((row) => (
				<div className={classes.row} key={row.key}>
					{row.cards.map((t) => (
						<div className={classes.cell} key={t.name}>
							{renderCard(t, { fill: true })}
						</div>
					))}
					{row.spacerKeys.map((spacerKey) => (
						<div className={classes.spacer} key={spacerKey} />
					))}
				</div>
			))}
			{barTransition && (
				<div className={classes.bar}>
					{renderCard(barTransition, { fill: true, bar: true })}
				</div>
			)}
			<div className={classes.hint}>{keyListener}</div>
		</div>
	);

	const gridLayout = (
		<Grid container spacing={1}>
			{gridTransitions.map((t) => (
				<Grid item xs={12} sm={sm} key={t.name}>
					{renderCard(t)}
				</Grid>
			))}
			{barTransition && (
				<Grid item xs={12} key={barTransition.name}>
					{renderCard(barTransition, { bar: true })}
				</Grid>
			)}
			<Grid item xs={12}>
				{keyListener}
			</Grid>
		</Grid>
	);

	return fitHeight ? fitLayout : gridLayout;
};

export default DynamicActions;
